// Templated push notifications an admin can broadcast to drivers in a city.
//
// This is sent as a real Firebase push via the backend's existing
// `POST /notifications/` endpoint (services/notification) — the same one
// Retool used. That endpoint takes `{ requests: [{ deviceToken, title, body,
// sound, data }] }` per driver; it doesn't resolve cohorts or template
// anything itself, so `title`/`body` here are the literal push copy, and
// `event` is just an identifier carried in `data` for the app to key off if
// it wants to deep-link on tap — it has no effect on delivery or cohort.
//
// `fields` are extra inputs the admin fills in for this template (e.g. the
// trip route), interpolated into `buildPreview` and passed through as `data`.
// Every field is a city picked from the operational city list (`pillLabel` is
// the short name shown on the picker pill); only the city's name goes into
// the push copy.
// The recipient cohort (which city's drivers get the message) is chosen
// separately — note the "change city" template, which is sent to drivers in
// a *different* city than the trip's pickup so they can claim a nearby
// small-city trip.
//
// Whether the trip is today or tomorrow applies to every template: it's picked
// from a toggle (see `TripDayOptions`), woven into every `buildPreview` copy as
// `when`, and sent through as `when` so the app can key off it.
export const TripDayOptions = [
  { key: 'today', label: 'Trip today' },
  { key: 'tomorrow', label: 'Trip tomorrow' },
];

// Which drivers a broadcast reaches. The backend only knows two mutually
// exclusive segments — `xlOnly: false` (drivers WITHOUT the XL tag) and
// `xlOnly: true` (ONLY drivers tagged XL) — and records each send as one or the
// other. "All drivers" is therefore two sends, one per segment, which is why
// each option lists the `xlOnly` flags it fans out to. Order matters: the
// non-XL send goes first.
export const DriverSegmentOptions = [
  { key: 'standard', label: 'Non-XL', xlOnlyFlags: [false] },
  { key: 'xl', label: 'XL only', xlOnlyFlags: [true] },
  { key: 'all', label: 'All drivers', xlOnlyFlags: [false, true] },
];

export const getDriverSegment = (key) => DriverSegmentOptions.find((s) => s.key === key) || DriverSegmentOptions[0];

// Human name for one backend segment, used in partial-failure messages.
export const xlSegmentName = (xlOnly) => (xlOnly ? 'XL' : 'non-XL');

export const DriverNotificationTemplates = [
  {
    value: 'trip_available_generic',
    label: 'Trip available (generic)',
    event: 'admin_driver_trip_available',
    title: 'Trip available',
    description: 'A generic nudge that there are trips available to claim in the app.',
    fields: [],
    buildPreview: ({ when = 'today' } = {}) =>
      `Hey there! Trip available ${when} in the app — Take it and let’s get on the road!`,
  },
  {
    value: 'trip_available_route',
    label: 'Trip available (specific route)',
    event: 'admin_driver_trip_available_route',
    title: 'Trip available',
    description: 'Tell drivers about a specific trip from one city to another.',
    fields: [
      { name: 'origin', label: 'From (pickup city)', pillLabel: 'From' },
      { name: 'destination', label: 'To (drop-off city)', pillLabel: 'To' },
    ],
    buildPreview: ({ origin, destination, when = 'today' } = {}) =>
      `Trip available ${when} from ${origin || '[from]'} to ${destination || '[to]'}. Click to accept.`,
  },
  {
    value: 'trip_available_change_city',
    label: 'Trip available (change city — for nearby/small-city trips)',
    event: 'admin_driver_trip_available_change_city',
    title: 'Trip available',
    description:
      'Send to drivers in a larger city to claim a trip in a nearby small city. Prompts them to change their city in the app first.',
    fields: [
      { name: 'origin', label: 'From (pickup city)', pillLabel: 'From' },
      { name: 'destination', label: 'To (drop-off city)', pillLabel: 'To' },
    ],
    buildPreview: ({ origin, destination, when = 'today' } = {}) =>
      `Trip available ${when} from ${origin || '[from]'} to ${destination || '[to]'}. Change the city in app and tap to claim!`,
  },
  {
    // Free-text copy for anything the fixed templates don't cover (a quest, an
    // ops announcement). `custom` makes the page show Title/Message inputs in
    // place of a fixed title, and drops the trip-timing toggle, which only
    // feeds the fixed templates' wording. `adminOnly`: free text goes to every
    // driver in a city verbatim, so only full ADMINs get it, not every admin
    // with `send_driver_notifications`.
    value: 'custom',
    label: 'Custom message',
    event: 'admin_driver_custom',
    title: '',
    custom: true,
    adminOnly: true,
    description: 'Write your own title and message.',
    fields: [],
    buildPreview: ({ body } = {}) => body || '[message]',
  },
];

export const getDriverNotificationTemplate = (value) =>
  DriverNotificationTemplates.find((t) => t.value === value) || null;

// The templates this admin may use: `adminOnly` ones are dropped unless they
// hold the full ADMIN permission. UI-only, like every other admin permission —
// the admin backend authenticates the caller but reads no permissions.
export const availableDriverNotificationTemplates = ({ isAdmin }) =>
  DriverNotificationTemplates.filter((t) => !t.adminOnly || isAdmin);
